import {
  memo,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { AppButton } from "../../../../../shared/components/AppButton";
import { AppIconButton } from "../../../../../shared/components/AppIconButton";
import { AppLabel } from "../../../../../shared/components/AppLabel";
import { AppNewInput } from "../../../../../shared/components/AppNewInput";
import { MinusIcon } from "../../../../../shared/icons/MinusIcon";
import { TSkillField } from "../../../types";
import styles from "./styles.module.css";
import { isLatinAndStandardChars } from "../../../../../shared/utils/isLatinAndStandardChars";
import { useAppDispatch } from "../../../../../services/redux/hooks/use-dispatch";
import { createNotification } from "../../../../../app/state/notificationSlice/slice";
import { generateId } from "../../../../../shared/utils/generateId";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

interface Props {
  field: TSkillField;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: string,
    type: "key" | "value",
  ) => void;
  onDelete: (id: string) => void;
  handleConfirm: (id: string) => void;
}

const MetadataFieldInner = ({
  field,
  onChange,
  onDelete,
  handleConfirm,
}: Props) => {
  const dispatch = useAppDispatch();
  const [errorInput, setErrorInput] = useState<boolean>(false);
  const label = field?.parent
    ? `${field.parent ?? ""}/${field.label.toLowerCase() ?? ""}`
    : (field.label.toUpperCase() ?? "");
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const hasNotifiedRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const blockRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (blockRef) {
      requestAnimationFrame(() => {
        blockRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      });
    }
    if (blockRef.current) {
    }
  }, []);

  useEffect(() => {
    if (isTyping && buttonRef.current) {
      requestAnimationFrame(() => {
        buttonRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      });
    }
    if (blockRef.current) {
    }
  }, [isTyping]);

  const handleBeforeInput = useCallback(
    (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
      const inputData = e.data;

      if (!inputData) return;

      if (!isLatinAndStandardChars(inputData)) {
        e.preventDefault();

        setErrorInput(true);
        if (!hasNotifiedRef.current) {
          hasNotifiedRef.current = true;

          dispatch(
            createNotification("warning", t("skills.input_warning"), 3000),
          );
        }
        return;
      }

      if (hasNotifiedRef.current) {
        hasNotifiedRef.current = false;
      }

      if (errorInput) {
        setErrorInput(false);
      }
    },
    [dispatch, errorInput],
  );

  const handlePaste = (e: React.ClipboardEvent) => {
    setErrorInput(false);
    const pastedText = e.clipboardData.getData("text");

    if (!isLatinAndStandardChars(pastedText)) {
      setErrorInput(true);
      e.preventDefault();
      dispatch(createNotification("warning", t("skills.input_warning"), 3000));
    }
  };
  return (
    <div ref={blockRef} className={styles["metadata-field-container"]}>
      <AppLabel>{label}</AppLabel>
      <div className={styles["metadata-inputs-container"]}>
        <AppNewInput
          value={field.label}
          onChange={(e) => {
            onChange(e, field.id, "key");
            setIsTyping(true);
          }}
          onBeforeInput={(e) => handleBeforeInput(e)}
          onPaste={(e) => handlePaste(e)}
          placeholder={field.placeholder}
          className={classNames(
            styles["label-input"],
            errorInput && styles["error-input"],
          )}
        />
        <AppNewInput
          value={field.value}
          onChange={(e) => {
            onChange(e, field.id, "value");
            setIsTyping(true);
          }}
          placeholder={field.placeholder}
          className={styles["value-input"]}
        />
        <AppIconButton onClick={() => onDelete(field.id)}>
          <MinusIcon />
        </AppIconButton>
      </div>
      {isTyping && (
        <AppButton
          ref={buttonRef}
          buttonType={"contained"}
          onClick={() => {
            handleConfirm(field.id);
            setIsTyping(false);
          }}
          className={styles["confirm-btn"]}
          disabled={field.value === "" || field.label === ""}
        >
          {t("common.confirm_button")}
        </AppButton>
      )}
    </div>
  );
};

export const MetadataField = memo(MetadataFieldInner);
